import { LitElement, html } from 'lit'

class AboutSection extends LitElement {
    // Use open DOM for Tailwind styling
    createRenderRoot() {
        return this
    }

    render() {
        return html`
            <section class="section-padding bg-gray-50 dark:bg-gray-800">
                <div class="container-custom">
                    <div class="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        <!-- Content -->
                        <div class="order-2 lg:order-1">
                            <h2 class="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-8">
                                The Future of Work is 
                                <span class="gradient-text">Automated</span>
                            </h2>
                            
                            <div class="prose prose-lg max-w-none">
                                <p class="text-xl text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
                                    JigsAI represents a paradigm shift in how we approach productivity and task management. 
                                    By harnessing the power of advanced artificial intelligence, we've created a platform 
                                    that doesn't just automate tasks—it understands your workflow and evolves with your needs.
                                </p>
                                
                                <p class="text-lg text-gray-600 dark:text-gray-400 leading-relaxed mb-8">
                                    Our mission is simple: eliminate the mundane so you can focus on the meaningful. 
                                    Whether you're a solo entrepreneur or part of a large enterprise, JigsAI adapts to 
                                    your unique workflow, learning patterns and optimizing processes in real-time.
                                </p>
                            </div>

                            <!-- Key Stats -->
                            <div class="grid grid-cols-3 gap-8 mb-8">
                                <div class="text-center">
                                    <div class="text-3xl font-bold text-primary-600 dark:text-primary-400 mb-2">10M+</div>
                                    <div class="text-sm text-gray-600 dark:text-gray-400">Tasks Automated</div>
                                </div>
                                <div class="text-center">
                                    <div class="text-3xl font-bold text-primary-600 dark:text-primary-400 mb-2">500K+</div>
                                    <div class="text-sm text-gray-600 dark:text-gray-400">Happy Users</div>
                                </div>
                                <div class="text-center">
                                    <div class="text-3xl font-bold text-primary-600 dark:text-primary-400 mb-2">99.9%</div>
                                    <div class="text-sm text-gray-600 dark:text-gray-400">Uptime</div>
                                </div>
                            </div>

                            <!-- Benefits List -->
                            <div class="space-y-4 mb-8">
                                <div class="flex items-start space-x-3">
                                    <div class="flex-shrink-0 w-6 h-6 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mt-0.5">
                                        <sl-icon library="fa-solid" name="fa-check" class="text-sm text-primary-600 dark:text-primary-400"></sl-icon>
                                    </div>
                                    <span class="text-gray-700 dark:text-gray-300">
                                        <strong>Save 10+ hours per week</strong> on repetitive tasks
                                    </span>
                                </div>
                                <div class="flex items-start space-x-3">
                                    <div class="flex-shrink-0 w-6 h-6 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mt-0.5">
                                        <sl-icon library="fa-solid" name="fa-check" class="text-sm text-primary-600 dark:text-primary-400"></sl-icon>
                                    </div>
                                    <span class="text-gray-700 dark:text-gray-300">
                                        <strong>Reduce human error</strong> by up to 95% with AI precision
                                    </span>
                                </div>
                                <div class="flex items-start space-x-3">
                                    <div class="flex-shrink-0 w-6 h-6 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mt-0.5">
                                        <sl-icon library="fa-solid" name="fa-check" class="text-sm text-primary-600 dark:text-primary-400"></sl-icon>
                                    </div>
                                    <span class="text-gray-700 dark:text-gray-300">
                                        <strong>Scale effortlessly</strong> as your business grows
                                    </span>
                                </div>
                            </div>

                            <sl-button
                                variant="primary"
                                size="large"
                                @click="${this.handleLearnMore}">
                                <sl-icon slot="prefix" library="fa-solid" name="fa-book"></sl-icon>
                                Learn More About Our Technology
                            </sl-button>
                        </div>

                        <!-- Visual/Illustration -->
                        <div class="order-1 lg:order-2">
                            <div class="relative">
                                <!-- Placeholder for illustration/image -->
                                <div class="aspect-square bg-gradient-to-br from-primary-100 via-blue-50 to-purple-100 dark:from-primary-900/20 dark:via-blue-900/20 dark:to-purple-900/20 rounded-3xl flex items-center justify-center relative overflow-hidden">
                                    <!-- Animated background elements -->
                                    <div class="absolute inset-0">
                                        <div class="absolute top-1/4 left-1/4 w-16 h-16 bg-primary-200 dark:bg-primary-800/50 rounded-full animate-pulse"></div>
                                        <div class="absolute top-3/4 right-1/4 w-12 h-12 bg-blue-200 dark:bg-blue-800/50 rounded-full animate-pulse delay-300"></div>
                                        <div class="absolute bottom-1/4 left-1/3 w-8 h-8 bg-purple-200 dark:bg-purple-800/50 rounded-full animate-pulse delay-700"></div>
                                    </div>
                                    
                                    <!-- Central icon -->
                                    <div class="relative z-10 w-32 h-32 bg-white dark:bg-gray-800 rounded-2xl shadow-xl flex items-center justify-center">
                                        <sl-icon library="fa-solid" name="fa-microchip" class="text-6xl text-primary-600 dark:text-primary-400"></sl-icon>
                                    </div>

                                    <!-- Floating elements -->
                                    <div class="absolute top-8 right-8 w-12 h-12 bg-white dark:bg-gray-800 rounded-lg shadow-lg flex items-center justify-center animate-bounce">
                                        <sl-icon library="fa-solid" name="fa-gear" class="text-xl text-gray-600 dark:text-gray-400"></sl-icon>
                                    </div>
                                    <div class="absolute bottom-8 left-8 w-12 h-12 bg-white dark:bg-gray-800 rounded-lg shadow-lg flex items-center justify-center animate-bounce delay-500">
                                        <sl-icon library="fa-solid" name="fa-bolt" class="text-xl text-gray-600 dark:text-gray-400"></sl-icon>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        `
    }

    handleLearnMore() {
        // Navigate to documentation or detailed information
        console.log('Learn more clicked')
    }
}

customElements.define('about-section', AboutSection)